/** Iter 232 · Admin PIP management page — /app/hrms/pip.
 *
 *  Owner/admin only. Lists all PIP plans in the workspace with counts of
 *  active/passed/failed. From here HR can initiate a new plan or drill into
 *  an existing one to add checkpoints, rate them, extend, or close.
 */
import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, ShieldAlert, ArrowLeft, Save, Check, X, Calendar, Target, Circle, CircleDot, CheckCircle2, AlertTriangle, Zap, Sparkles, Trophy } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const STATUS_BADGE = {
  active:   "bg-amber-100 text-amber-800 border-amber-400",
  passed:   "bg-emerald-100 text-emerald-800 border-emerald-400",
  failed:   "bg-rose-100 text-rose-800 border-rose-400",
  extended: "bg-sky-100 text-sky-800 border-sky-400",
};

const RATING_CLS = {
  green: "bg-emerald-100 text-emerald-800 border-emerald-400",
  amber: "bg-amber-100 text-amber-800 border-amber-400",
  red:   "bg-rose-100 text-rose-800 border-rose-400",
};

const iso = (d) => (d instanceof Date ? d : new Date(d)).toISOString().slice(0, 10);
const addDays = (isoDate, n) => { const d = new Date(isoDate); d.setDate(d.getDate() + n); return iso(d); };

export default function PIPAdminPage() {
  const { activeWorkspaceId, activeWorkspace, user } = useAuth();
  const role = activeWorkspace?.role || activeWorkspace?.membership_role;
  const isAdmin = user?.is_super_admin || role === "owner" || role === "admin";

  const [sp, setSp] = useSearchParams();
  const openId = sp.get("id");

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initiateOpen, setInitiateOpen] = useState(false);
  const [initiatePrefill, setInitiatePrefill] = useState(null);
  const [convertingRecId, setConvertingRecId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [flags, setFlags] = useState([]);
  const [dutyRecs, setDutyRecs] = useState([]);
  const [runningSweep, setRunningSweep] = useState(false);
  const [stories, setStories] = useState([]);

  const load = async () => {
    if (!activeWorkspaceId || !isAdmin) return;
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const [pRes, fRes, sRes, drRes] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/pip-plans`, { params }),
        api.get(`/workspaces/${activeWorkspaceId}/pip-risk-flags`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/pip-recovery-stories`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/hr/pip-recommendations`).catch(() => ({ data: [] })),
      ]);
      setPlans(pRes.data || []);
      setFlags(fRes.data || []);
      setStories(sRes.data || []);
      setDutyRecs(drRes.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load PIP plans"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, isAdmin, statusFilter]);

  const runSweepNow = async () => {
    setRunningSweep(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/pip-auto-flag/run`, { dry_run: false });
      toast.success(`Sweep complete · ${data.flags_created} flag(s), scanned ${data.employees_scanned} employees`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Sweep failed"));
    } finally { setRunningSweep(false); }
  };

  const clearFlag = async (fid) => {
    try {
      await api.post(`/pip-risk-flags/${fid}/clear`);
      toast.success("Flag cleared");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    }
  };

  const dismissDutyRec = async (rid) => {
    try {
      await api.post(`/hr/pip-recommendations/${rid}/dismiss`);
      toast.success("Recommendation dismissed");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    }
  };

  const convertDutyRec = (rec) => {
    setConvertingRecId(rec.id);
    setInitiatePrefill({
      assignee_id: rec.user_id,
      title: `PIP for ${rec.user_name || "employee"}`,
      reason_summary:
        `Auto-flagged by Job Duty Reviews: ${rec.below_count} 'Below' ratings across the last ${rec.window_reviews} reviews.` +
        (rec.duty_titles?.length ? ` Impacted duties: ${rec.duty_titles.join(", ")}.` : ""),
    });
    setInitiateOpen(true);
  };

  const counts = useMemo(() => {
    const c = { active: 0, passed: 0, failed: 0, extended: 0 };
    plans.forEach((p) => { c[p.status] = (c[p.status] || 0) + 1; });
    return c;
  }, [plans]);

  if (!isAdmin) {
    return (
      <div className="space-y-4">
        <PageHeader title="Performance Improvement Plans" subtitle="Manage active and historical PIPs." />
        <div className="border border-border p-8 text-sm text-muted-foreground" data-testid="pip-admin-forbidden">
          Only workspace owners/admins can view this page.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pip-admin-page">
      <PageHeader
        title="Performance Improvement Plans"
        subtitle="Formal HR process for coaching under-performing team members. Broad visibility: assignee + manager + admins."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={runSweepNow} disabled={runningSweep}
                       className="rounded-none" data-testid="pip-run-sweep-btn">
              {runningSweep ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Zap className="w-4 h-4 mr-1.5" />}
              Run at-risk sweep
            </Button>
            <Button onClick={() => setInitiateOpen(true)} className="rounded-none" data-testid="pip-initiate-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Initiate PIP
            </Button>
          </div>
        }
      />

      {flags.length > 0 && (
        <div className="border-2 border-amber-400 bg-amber-50/40 p-4 space-y-2" data-testid="pip-risk-flags-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
            <div className="text-sm font-bold">
              {flags.length} at-risk employee{flags.length === 1 ? "" : "s"} flagged by the nightly sweep
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            These employees have breached the enabled PIP eligibility signals. Review before things get formal — a conversation now often prevents a PIP later.
          </p>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {flags.map((flag) => (
              <div key={flag.id} className="bg-background border border-amber-300 p-2.5 flex items-center gap-3"
                     data-testid={`pip-flag-${flag.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {flag.evidence?.signals ? Object.entries(flag.evidence.signals).filter(([k,v]) => v.breach).map(([k,v]) => `${k}: ${v.value}${v.unit || ''}`).join(" · ") : ""}
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono">
                    User {flag.user_id?.slice(0, 8)} · flagged {flag.flagged_at?.slice(0, 10)}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => clearFlag(flag.id)}
                            className="rounded-none h-7 text-[10px]" data-testid={`pip-flag-${flag.id}-clear`}>
                  Clear
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Active" value={counts.active || 0} tone="amber" />
        <StatCard label="Passed" value={counts.passed || 0} tone="emerald" />
        <StatCard label="Extended" value={counts.extended || 0} tone="sky" />
        <StatCard label="Not passed" value={counts.failed || 0} tone="rose" />
      </div>

      <div className="flex items-center gap-2">
        <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Filter:</label>
        {["", "active", "passed", "failed"].map((s) => (
          <button
            key={s || "all"}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-2.5 py-1 border ${statusFilter === s ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
            data-testid={`pip-filter-${s || "all"}`}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>
      ) : plans.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground text-sm" data-testid="pip-empty">
          No PIPs in this workspace yet. Click <span className="font-semibold">Initiate PIP</span> to create the first one.
        </div>
      ) : (
        <div className="border border-border" data-testid="pip-list">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-left text-[10px] uppercase text-muted-foreground">
                <th className="px-3 py-2">Employee</th>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2 w-40">Window</th>
                <th className="px-3 py-2 w-24">Status</th>
                <th className="px-3 py-2 w-24">Outcome</th>
                <th className="px-3 py-2 w-20 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {plans.map((p) => (
                <tr key={p.id} className="hover:bg-muted/30 cursor-pointer"
                       onClick={() => setSp({ id: p.id })}
                       data-testid={`pip-row-${p.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{p.assignee_name}</div>
                    <div className="text-[10px] text-muted-foreground">{p.assignee_email}</div>
                  </td>
                  <td className="px-3 py-2 truncate max-w-xs">{p.title}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{p.start_date} → {p.end_date}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${STATUS_BADGE[p.status] || "bg-muted border-border"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">{p.outcome || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={(e) => { e.stopPropagation(); setSp({ id: p.id }); }}
                              className="text-primary text-xs font-semibold hover:underline"
                              data-testid={`pip-open-${p.id}`}>Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dutyRecs.length > 0 && (
        <div className="border-2 border-purple-400 bg-purple-50/40 p-4 space-y-2" data-testid="pip-duty-recommendations">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-purple-700" />
            <div className="text-sm font-bold">
              {dutyRecs.length} auto-recommend{dutyRecs.length === 1 ? "s" : ""} from Job Duty reviews
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Employees with a rolling streak of &quot;Below&quot; duty ratings across their last 3 reviews. Convert to a formal PIP with pre-filled context, or dismiss.
          </p>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {dutyRecs.map((rec) => (
              <div key={rec.id}
                    className="bg-background border border-purple-300 p-2.5 flex items-center gap-3"
                    data-testid={`pip-duty-rec-${rec.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{rec.user_name || rec.user_email}</div>
                  <div className="text-[11px] text-muted-foreground">
                    <b>{rec.below_count}</b> Below in last {rec.window_reviews} reviews
                    {rec.duty_titles?.length ? ` · ${rec.duty_titles.join(", ")}` : ""}
                  </div>
                </div>
                <Button size="sm" onClick={() => convertDutyRec(rec)}
                              className="rounded-none h-7 text-[10px] bg-purple-600 hover:bg-purple-700"
                              data-testid={`pip-duty-rec-${rec.id}-convert`}>
                  Convert to PIP
                </Button>
                <Button size="sm" variant="outline" onClick={() => dismissDutyRec(rec.id)}
                              className="rounded-none h-7 text-[10px]"
                              data-testid={`pip-duty-rec-${rec.id}-dismiss`}>
                  Dismiss
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {initiateOpen && (
        <InitiatePIPDialog
          wsId={activeWorkspaceId}
          prefill={initiatePrefill}
          onClose={() => { setInitiateOpen(false); setInitiatePrefill(null); setConvertingRecId(null); }}
          onCreated={async (p) => {
            if (convertingRecId) {
              try {
                await api.post(`/hr/pip-recommendations/${convertingRecId}/mark-converted`,
                                 null, { params: { plan_id: p.id } });
              } catch { /* non-fatal */ }
            }
            setInitiateOpen(false); setInitiatePrefill(null); setConvertingRecId(null);
            load(); setSp({ id: p.id });
          }}
        />
      )}

      {stories.length > 0 && (
        <div className="border border-emerald-300 bg-emerald-50/40 p-4 space-y-2" data-testid="pip-recovery-archive">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-emerald-600" />
            <div className="text-sm font-bold">Recovery archive — {stories.length} success{stories.length === 1 ? "" : "es"}</div>
          </div>
          <p className="text-xs text-muted-foreground">
            Anonymous stories from PIPs that closed successfully. Employees see success is possible, not just warnings.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
            {stories.map((s) => (
              <div key={s.id} className="bg-background border border-emerald-300 p-3 text-xs"
                     data-testid={`pip-recovery-${s.id}`}>
                <div className="font-medium text-sm text-emerald-800 mb-1">{s.title || "Successful improvement"}</div>
                <div className="text-[10px] text-muted-foreground font-mono">
                  {s.duration_days ? `${s.duration_days}d` : "—"} · {s.checkpoint_count || 0} checkpoints
                  {s.first_rating && s.final_rating && ` · ${s.first_rating.toUpperCase()} → ${s.final_rating.toUpperCase()}`}
                </div>
                {s.goals?.length > 0 && (
                  <ul className="mt-2 space-y-0.5 text-[11px]">
                    {s.goals.slice(0, 3).map((g, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <Target className="w-2.5 h-2.5 mt-0.5 text-emerald-600 shrink-0" /> {g}
                      </li>
                    ))}
                  </ul>
                )}
                {s.close_note_snippet && (
                  <div className="mt-2 text-[11px] italic text-muted-foreground border-l-2 border-emerald-400 pl-2">
                    {s.close_note_snippet}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {openId && (
        <PIPDetailDialog
          pid={openId}
          onClose={() => setSp({})}
          onChanged={load}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const cls = {
    amber: "text-amber-600",
    emerald: "text-emerald-600",
    sky: "text-sky-600",
    rose: "text-rose-600",
  }[tone] || "text-foreground";
  return (
    <div className="border border-border p-4">
      <div className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

// ─────────────────── Initiate dialog ───────────────────

function InitiatePIPDialog({ wsId, onClose, onCreated, prefill = null }) {
  const [cfg, setCfg] = useState(null);
  const [members, setMembers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [draftingReason, setDraftingReason] = useState(false);
  const [f, setF] = useState({
    assignee_id: prefill?.assignee_id || "", manager_id: "",
    title: prefill?.title || "",
    reason_summary: prefill?.reason_summary || "",
    start_date: iso(new Date()),
    end_date: iso(addDays(new Date(), 60)),
    goals_text: "", first_checkpoint_at: iso(addDays(new Date(), 14)),
    consequence_on_fail: "",
  });
  const setV = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const loadTemplates = () => api.get(`/workspaces/${wsId}/pip-templates`)
    .then((r) => setTemplates(r.data || []))
    .catch(() => setTemplates([]));

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/settings/pip`).then((r) => {
      setCfg(r.data);
      const dur = r.data.default_duration_days || 60;
      const cp  = r.data.default_checkpoint_days || 14;
      setF((s) => ({
        ...s,
        end_date: iso(addDays(s.start_date, dur)),
        first_checkpoint_at: iso(addDays(s.start_date, cp)),
      }));
    }).catch(() => setCfg(null));
    api.get(`/workspaces/${wsId}/members`)
      .then((r) => {
        const flat = (r.data || [])
          .filter((m) => m.user && (m.user.name || m.user.email))
          .map((m) => ({
            user_id: m.user_id || m.user?.id,
            name: m.user?.name || m.name,
            email: m.user?.email || m.email,
            role: m.role,
          }));
        const seen = new Set();
        setMembers(flat.filter((m) => {
          if (!m.user_id || seen.has(m.user_id)) return false;
          seen.add(m.user_id); return true;
        }));
      })
      .catch(() => setMembers([]));
    loadTemplates();
  }, [wsId]);

  const applyTemplate = (tid) => {
    setSelectedTpl(tid);
    const tpl = templates.find((t) => t.id === tid);
    if (!tpl) return;
    const start = f.start_date;
    setF((s) => ({
      ...s,
      title: s.title || tpl.name,
      reason_summary: tpl.reason_summary || s.reason_summary,
      goals_text: (tpl.goals || []).join("\n") || s.goals_text,
      end_date: iso(addDays(start, tpl.duration_days || 60)),
      first_checkpoint_at: iso(addDays(start, tpl.checkpoint_days || 14)),
      consequence_on_fail: tpl.consequence_on_fail || s.consequence_on_fail,
    }));
    toast.success(`Applied template "${tpl.name}"`);
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Template name?");
    if (!name || !name.trim()) return;
    setSavingTpl(true);
    try {
      await api.post(`/workspaces/${wsId}/pip-templates`, {
        name: name.trim(),
        duration_days: Math.max(7, Math.round((new Date(f.end_date) - new Date(f.start_date)) / 86400000)),
        checkpoint_days: Math.max(1, Math.round((new Date(f.first_checkpoint_at) - new Date(f.start_date)) / 86400000)),
        goals: f.goals_text.split("\n").map((g) => g.trim()).filter(Boolean),
        reason_summary: f.reason_summary || null,
        consequence_on_fail: f.consequence_on_fail || null,
      });
      await loadTemplates();
      toast.success(`Saved template "${name.trim()}"`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSavingTpl(false); }
  };

  const deleteTemplate = async (tid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/pip-templates/${tid}`);
      await loadTemplates();
      toast.success("Template deleted");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Delete failed"));
    }
  };

  const draftReason = async () => {
    if (!f.assignee_id) { toast.error("Pick an assignee first"); return; }
    setDraftingReason(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/pip-plans/draft-reason`,
                                          { assignee_id: f.assignee_id });
      if (data?.reason_summary) {
        setV("reason_summary", data.reason_summary);
        if (data.warning) toast.warning(data.warning);
        else toast.success("Reason drafted from the assignee's current signals — please review before saving");
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Draft failed"));
    } finally { setDraftingReason(false); }
  };

  const submit = async () => {
    if (!f.assignee_id || !f.title.trim() || !f.reason_summary.trim()) {
      toast.error("Assignee, title and reason are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        assignee_id: f.assignee_id,
        manager_id: f.manager_id || null,
        title: f.title.trim(),
        reason_summary: f.reason_summary.trim(),
        start_date: f.start_date,
        end_date: f.end_date,
        goals: f.goals_text.split("\n").map((s) => s.trim()).filter(Boolean),
        first_checkpoint_at: f.first_checkpoint_at || null,
        consequence_on_fail: f.consequence_on_fail || null,
      };
      const { data } = await api.post(`/workspaces/${wsId}/pip-plans`, payload);
      toast.success("PIP initiated");
      onCreated(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Initiate failed"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="pip-initiate-dialog">
        <DialogHeader>
          <DialogTitle>Initiate a Performance Improvement Plan</DialogTitle>
          <DialogDescription>
            A PIP is a formal HR process. Keep it factual — assignee, manager (if applicable) and admins will see everything you enter.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {templates.length > 0 && (
            <div className="border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="pip-init-templates">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Start from a template</div>
              <div className="flex flex-wrap gap-2">
                {templates.map((t) => (
                  <div key={t.id}
                       className={`text-xs pl-2.5 pr-1 py-0.5 border inline-flex items-center gap-1 transition ${selectedTpl === t.id ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                       data-testid={`pip-init-tpl-${t.id}`}
                       title={`${t.duration_days}d · ${(t.goals || []).length} goals`}>
                    <button
                      type="button"
                      onClick={() => applyTemplate(t.id)}
                      className="py-1 hover:opacity-90"
                      data-testid={`pip-init-tpl-${t.id}-apply`}
                    >
                      {t.name}
                    </button>
                    <button type="button" onClick={(e) => deleteTemplate(t.id, e)}
                              className={`p-0.5 opacity-60 hover:opacity-100 ${selectedTpl === t.id ? "text-primary-foreground" : "text-muted-foreground"}`}
                              data-testid={`pip-init-tpl-${t.id}-del`}
                              title="Delete template">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Assignee *</Label>
              <Select value={f.assignee_id} onValueChange={(v) => setV("assignee_id", v)}>
                <SelectTrigger className="h-10" data-testid="pip-init-assignee"><SelectValue placeholder="Choose employee" /></SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.name || m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reporting manager</Label>
              <Select value={f.manager_id} onValueChange={(v) => setV("manager_id", v)}>
                <SelectTrigger className="h-10" data-testid="pip-init-manager"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.name || m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Plan title *</Label>
            <Input value={f.title} onChange={(e) => setV("title", e.target.value)}
                     placeholder="e.g. Q3 Sales Delivery Improvement"
                     data-testid="pip-init-title" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Reason summary *</Label>
              <button
                type="button"
                onClick={draftReason}
                disabled={draftingReason || !f.assignee_id}
                className="text-[11px] px-2 py-0.5 border border-primary/40 bg-primary/5 hover:bg-primary/10 disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="pip-init-draft-reason"
                title="Draft with AI using the assignee's current signal evidence"
              >
                {draftingReason ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Draft with AI
              </button>
            </div>
            <Textarea value={f.reason_summary} onChange={(e) => setV("reason_summary", e.target.value)}
                          placeholder="Objective, dated summary of the performance concerns."
                          rows={4} data-testid="pip-init-reason" />
          </div>

          <div>
            <Label>Improvement goals (one per line)</Label>
            <Textarea value={f.goals_text} onChange={(e) => setV("goals_text", e.target.value)}
                          placeholder={"Meet quarterly quota\nSubmit weekly reports on time\nClose all pending tickets within SLA"}
                          rows={4} data-testid="pip-init-goals" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Start</Label>
              <Input type="date" value={f.start_date}
                       onChange={(e) => setV("start_date", e.target.value)}
                       data-testid="pip-init-start" />
            </div>
            <div>
              <Label>End</Label>
              <Input type="date" value={f.end_date}
                       onChange={(e) => setV("end_date", e.target.value)}
                       data-testid="pip-init-end" />
            </div>
            <div>
              <Label>First checkpoint</Label>
              <Input type="date" value={f.first_checkpoint_at}
                       onChange={(e) => setV("first_checkpoint_at", e.target.value)}
                       data-testid="pip-init-checkpoint" />
            </div>
          </div>

          <div>
            <Label>Consequence if not passed</Label>
            <Select value={f.consequence_on_fail} onValueChange={(v) => setV("consequence_on_fail", v)}>
              <SelectTrigger className="h-10" data-testid="pip-init-consequence"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {(cfg?.consequences || []).map((c) => (
                  <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={saveAsTemplate} disabled={savingTpl || !f.goals_text.trim()}
                     className="rounded-none mr-auto" data-testid="pip-init-save-as-tpl">
            {savingTpl ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Save as template
          </Button>
          <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="pip-init-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-none" data-testid="pip-init-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
            Initiate PIP
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────── Detail / manage dialog ───────────────────

function PIPDetailDialog({ pid, onClose, onChanged }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [addingCp, setAddingCp] = useState(false);
  const [newCp, setNewCp] = useState({ scheduled_at: iso(new Date()), focus: "", notes: "" });
  const [closeForm, setCloseForm] = useState({ outcome: "passed", consequence: "", new_end_date: "", close_note: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pip-plans/${pid}`);
      setPlan(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load plan"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const addCheckpoint = async () => {
    if (!newCp.scheduled_at || !newCp.focus.trim()) { toast.error("Date + focus required"); return; }
    try {
      await api.post(`/pip-plans/${pid}/checkpoints`, newCp);
      setNewCp({ scheduled_at: iso(new Date()), focus: "", notes: "" });
      setAddingCp(false);
      await load();
      onChanged?.();
      toast.success("Checkpoint added");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    }
  };

  const rateCp = async (cid, patch) => {
    try {
      await api.patch(`/pip-plans/${pid}/checkpoints/${cid}`, patch);
      await load(); onChanged?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    }
  };

  const closeNow = async () => {
    if (!closeForm.close_note.trim()) { toast.error("Close note is required"); return; }
    if (closeForm.outcome === "failed" && !closeForm.consequence) { toast.error("Pick a consequence"); return; }
    if (closeForm.outcome === "extended" && !closeForm.new_end_date) { toast.error("Pick new end date"); return; }
    setClosing(true);
    try {
      await api.post(`/pip-plans/${pid}/close`, {
        outcome: closeForm.outcome,
        consequence: closeForm.consequence || null,
        new_end_date: closeForm.new_end_date || null,
        close_note: closeForm.close_note.trim(),
      });
      toast.success("PIP closed");
      await load(); onChanged?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    } finally { setClosing(false); }
  };

  if (loading || !plan) return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl rounded-none"><div className="p-8 text-center text-sm text-muted-foreground">Loading…</div></DialogContent>
    </Dialog>
  );

  const cps = plan.checkpoints || [];
  const isActive = plan.status === "active";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto rounded-none" data-testid={`pip-detail-${pid}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <ShieldAlert className="w-4 h-4 text-amber-600" />
            {plan.title}
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${STATUS_BADGE[plan.status]}`}>
              {plan.status}
            </span>
            {plan.outcome && (
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                outcome: {plan.outcome}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {plan.assignee_name} ({plan.assignee_email}) · <span className="font-mono">{plan.start_date} → {plan.end_date}</span>
          </DialogDescription>
        </DialogHeader>

        <section className="space-y-1">
          <div className="label-eyebrow">Reason</div>
          <p className="text-sm whitespace-pre-wrap">{plan.reason_summary}</p>
        </section>

        {(plan.goals || []).length > 0 && (
          <section className="space-y-1">
            <div className="label-eyebrow">Improvement goals</div>
            <ul className="space-y-1">
              {plan.goals.map((g, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <Target className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" /><span>{g}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Checkpoints</div>
            {isActive && !addingCp && (
              <Button size="sm" variant="outline" onClick={() => setAddingCp(true)} className="rounded-none h-7 text-xs" data-testid="pip-add-checkpoint-btn">
                <Plus className="w-3 h-3 mr-1" /> Add checkpoint
              </Button>
            )}
          </div>
          {addingCp && (
            <div className="border border-border p-3 space-y-2 bg-muted/20" data-testid="pip-add-checkpoint-form">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px]">Scheduled at</Label>
                  <Input type="date" value={newCp.scheduled_at} onChange={(e) => setNewCp((s) => ({ ...s, scheduled_at: e.target.value }))} className="h-8" data-testid="pip-new-cp-date" />
                </div>
                <div>
                  <Label className="text-[10px]">Focus</Label>
                  <Input value={newCp.focus} onChange={(e) => setNewCp((s) => ({ ...s, focus: e.target.value }))} placeholder="What's the theme of this checkpoint?" className="h-8" data-testid="pip-new-cp-focus" />
                </div>
              </div>
              <Textarea value={newCp.notes} onChange={(e) => setNewCp((s) => ({ ...s, notes: e.target.value }))} placeholder="Notes for the assignee (optional)" rows={2} data-testid="pip-new-cp-notes" />
              <div className="flex items-center justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setAddingCp(false)} className="rounded-none h-7 text-xs" data-testid="pip-new-cp-cancel">Cancel</Button>
                <Button size="sm" onClick={addCheckpoint} className="rounded-none h-7 text-xs" data-testid="pip-new-cp-save">Save checkpoint</Button>
              </div>
            </div>
          )}
          {cps.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No checkpoints yet.</p>
          ) : (
            <ol className="space-y-2">
              {cps.map((c) => {
                const Icon = c.completed ? CheckCircle2 : CircleDot;
                return (
                  <li key={c.id} className="border border-border p-3 space-y-1.5" data-testid={`pip-cp-${c.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon className={`w-4 h-4 ${c.completed ? "text-emerald-500" : "text-primary"}`} />
                      <span className="font-medium text-sm">{c.focus}</span>
                      <span className="text-[10px] text-muted-foreground font-mono">
                        <Calendar className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />
                        {c.scheduled_at}
                      </span>
                      {c.rating && (
                        <span className={`text-[10px] px-1.5 py-0.5 border font-bold uppercase ${RATING_CLS[c.rating]}`}>{c.rating}</span>
                      )}
                    </div>
                    {c.notes && <div className="text-xs text-muted-foreground">{c.notes}</div>}
                    {c.evidence && <div className="text-xs bg-muted/40 border border-border p-2">{c.evidence}</div>}
                    {isActive && (
                      <div className="flex items-center gap-2 flex-wrap pt-1">
                        {["green", "amber", "red"].map((r) => (
                          <Button key={r} size="sm" variant={c.rating === r ? "default" : "outline"}
                                     onClick={() => rateCp(c.id, { rating: r })}
                                     className="rounded-none h-7 text-[10px] px-2"
                                     data-testid={`pip-cp-${c.id}-rate-${r}`}>
                            {r.toUpperCase()}
                          </Button>
                        ))}
                        <Button size="sm" variant={c.completed ? "default" : "outline"}
                                   onClick={() => rateCp(c.id, { completed: !c.completed })}
                                   className="rounded-none h-7 text-[10px] px-2 ml-auto"
                                   data-testid={`pip-cp-${c.id}-toggle`}>
                          {c.completed ? <><Check className="w-3 h-3 mr-1" /> Completed</> : "Mark complete"}
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        {(plan.activity || []).length > 0 && (
          <section className="space-y-1">
            <div className="label-eyebrow">Activity</div>
            <ol className="space-y-1 max-h-40 overflow-y-auto border border-border p-2 bg-muted/20">
              {plan.activity.map((a) => (
                <li key={a.id} className="text-[11px] flex gap-2">
                  <span className="text-muted-foreground font-mono shrink-0">{a.at.slice(0, 16).replace("T", " ")}</span>
                  <span className="text-primary font-semibold">{a.action}</span>
                  <span className="text-muted-foreground truncate">· by {a.actor_name}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {isActive && (
          <section className="border-t border-border pt-4 space-y-2">
            <div className="label-eyebrow">Close this PIP</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Outcome</Label>
                <Select value={closeForm.outcome} onValueChange={(v) => setCloseForm((s) => ({ ...s, outcome: v }))}>
                  <SelectTrigger className="h-9" data-testid="pip-close-outcome"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="passed">Passed</SelectItem>
                    <SelectItem value="extended">Extended</SelectItem>
                    <SelectItem value="failed">Not passed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {closeForm.outcome === "extended" && (
                <div>
                  <Label className="text-[10px]">New end date</Label>
                  <Input type="date" value={closeForm.new_end_date}
                            onChange={(e) => setCloseForm((s) => ({ ...s, new_end_date: e.target.value }))}
                            className="h-9" data-testid="pip-close-new-end" />
                </div>
              )}
              {closeForm.outcome === "failed" && (
                <div>
                  <Label className="text-[10px]">Consequence</Label>
                  <Select value={closeForm.consequence} onValueChange={(v) => setCloseForm((s) => ({ ...s, consequence: v }))}>
                    <SelectTrigger className="h-9" data-testid="pip-close-consequence"><SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="formal_warning">Formal warning</SelectItem>
                      <SelectItem value="extended_pip">Extended PIP</SelectItem>
                      <SelectItem value="exit">Exit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <Label className="text-[10px]">Close note (visible to assignee)</Label>
              <Textarea value={closeForm.close_note}
                             onChange={(e) => setCloseForm((s) => ({ ...s, close_note: e.target.value }))}
                             placeholder="Summarize the outcome and immediate next step."
                             rows={3} data-testid="pip-close-note" />
            </div>
            <div className="flex items-center justify-end">
              <Button onClick={closeNow} disabled={closing} className="rounded-none" data-testid="pip-close-submit">
                {closing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                Close PIP
              </Button>
            </div>
          </section>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="pip-detail-close">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to list
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
